// ────────────────────────────────────────────────────────────────────────────
// The order's checkpoints, in a panel that slides in from the right.
//
// The list is assembled by the server from Settings → Trip Checkpoints every
// time it is read — a new stop, a tag, a template change shows at once — and
// only what people did is stored. The planner ticks off their own items (with
// an answer or a value where the kind has one), takes an item off the order
// with a reason (Not applicable: any planner; Waive: Manager and above; a
// locked item allows neither), and adds one-offs for this order alone. Driver
// and subcontractor items wait for the phone.
//
// NOTHING HERE BLOCKS. "Due" and "Overdue" are warnings against the order's
// next status; a failed answer warns too.
// ────────────────────────────────────────────────────────────────────────────
import { useAddressesByIds, usePermissions } from '@my/directory-api/hooks';
import type {
  OrderCheckpointItem,
  OrderCheckpointRecord,
  OrderCheckpoints,
  TripCheckpointDueValue,
  TripCheckpointKindValue,
} from '@my/tms-api';
import { useOrderCheckpointActions } from '@my/tms-api/hooks';
import { ErrorBanner, Loading, cn, showToast } from '@my/ui';
import { Ban, Check, Circle, Loader2, Lock, MinusCircle, Plus, Send, Trash2, Undo2, X } from 'lucide-react';
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { TaskTypeBadge } from '@/features/orders/components/badges';
import { adaptTaskType } from '@/features/orders/lib/adapters';
import { taskTypeLabel } from '@/features/orders/lib/taskTypeConfig';
import { RoleTag, RoleToggles } from '@/features/settings/trip-checkpoints/components/roles';
import { CheckpointEvidence } from './CheckpointEvidence';
import {
  DUE_LABEL,
  DUE_ORDER,
  KIND_ORDER,
  KINDS,
  MOMENT_LABEL,
  MOMENT_ORDER,
  type CheckpointKind,
  type Due,
  type Moment,
  type Role,
} from '@/features/settings/trip-checkpoints/lib/model';
import { checkpointFromWire, roleFromWire, roleToWire } from '@/features/settings/trip-checkpoints/lib/wire';

/** Kinds where the planner's Done carries a pass / fail answer. */
const ANSWER_KINDS: readonly CheckpointKind[] = ['yesNo', 'inspection'];
/** Kinds where the planner's Done carries a typed value. */
const VALUE_KINDS: readonly CheckpointKind[] = ['identifier', 'reading', 'note', 'quantity'];

const STATUS_LABEL: Record<string, string> = {
  Draft: 'Draft',
  Open: 'Open',
  InProgress: 'In progress',
  Completed: 'Completed',
  Cancelled: 'Cancelled',
};

const when = (iso: string) =>
  new Date(iso).toLocaleString(undefined, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });

export function OrderCheckpointsPanel({
  open,
  onClose,
  organisationId,
  orderId,
  data,
  loading,
  error,
  onSendToDriver,
}: {
  open: boolean;
  onClose: () => void;
  /** Opens Driver jobs on the send screen — a shortcut from here. */
  onSendToDriver?: () => void;
  organisationId: string | null;
  orderId: string | null;
  data: OrderCheckpoints | undefined;
  loading: boolean;
  error: Error | null;
}) {
  const actions = useOrderCheckpointActions(organisationId, orderId);
  const { role } = usePermissions();
  const canWaive = role === 'Owner' || role === 'Admin' || role === 'Manager' || role === 'SuperUser';

  const addressIds = useMemo(
    () => (data?.tasks ?? []).map((t) => t.addressId).filter((id): id is string => !!id),
    [data],
  );
  const addressesQuery = useAddressesByIds(open ? organisationId : null, addressIds);
  const placeOf = (addressId: string | null) => {
    const a = addressId ? addressesQuery.data?.find((x) => x.id === addressId) : undefined;
    return a ? [a.name?.trim(), a.city?.trim()].filter(Boolean).join(', ') || a.fullAddress : null;
  };

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  /** Runs an action and reports a failure; the success re-renders from the answer. */
  const run = async (action: () => Promise<unknown>, failed: string) => {
    try {
      await action();
    } catch (err) {
      showToast({ type: 'error', message: err instanceof Error ? err.message : failed });
    }
  };

  const ctx: ItemContext = { actions, run, canWaive, organisationId, orderId };

  return (
    <>
      <div
        aria-hidden
        onClick={onClose}
        className={cn(
          'fixed inset-0 z-40 bg-slate-900/20 transition-opacity',
          open ? 'opacity-100' : 'pointer-events-none opacity-0',
        )}
      />
      <aside
        role="dialog"
        aria-modal="true"
        aria-label="Checkpoints"
        className={cn(
          'fixed inset-y-0 right-0 z-50 flex w-[30rem] max-w-[94%] flex-col border-l border-slate-200 bg-white shadow-2xl transition-transform duration-300 ease-out',
          open ? 'translate-x-0' : 'pointer-events-none translate-x-full',
        )}
      >
        <header className="flex items-start justify-between gap-3 border-b border-slate-200 px-4 py-3">
          <div className="min-w-0">
            <h2 className="text-sm font-semibold text-slate-900">
              Checkpoints{data ? ` · ${data.orderNumber}` : ''}
            </h2>
            {data && <Summary data={data} />}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
          >
            <X className="h-4 w-4" />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto">
          {loading && !data && (
            <div className="p-4">
              <Loading label="Loading checkpoints…" />
            </div>
          )}
          {error && (
            <div className="p-4">
              <ErrorBanner title="Couldn't load the checkpoints" message={error.message} />
            </div>
          )}

          {data && (
            <div className="divide-y divide-slate-100">
              <Section
                title="Order"
                subtitle={`Order is ${STATUS_LABEL[data.orderStatus] ?? data.orderStatus} — order checks are due before its next status`}
              >
                <Items items={data.order} orderTaskId={null} ctx={ctx} />
                <AddOneOff orderTaskId={null} ctx={ctx} />
              </Section>

              {data.tasks.map((task) => (
                <Section
                  key={task.orderTaskId}
                  title={
                    <span className="flex min-w-0 items-center gap-2">
                      <TaskTypeBadge type={adaptTaskType(task.taskType)} label={taskTypeLabel(task.taskType)} />
                      <span className="truncate">{placeOf(task.addressId) ?? `Stop ${task.sequence}`}</span>
                    </span>
                  }
                  subtitle={
                    task.doneBy === 'Driver'
                      ? `Our driver${task.who ? ` · ${task.who}` : ''}`
                      : task.doneBy === 'Sub'
                        ? `Subcontracted${task.who ? ` · ${task.who}` : ''}`
                        : 'Not assigned yet'
                  }
                >
                  <Items items={task.items} orderTaskId={task.orderTaskId} ctx={ctx} />
                  <AddOneOff orderTaskId={task.orderTaskId} ctx={ctx} />
                </Section>
              ))}

              {data.matches.every((m) => !m.matched) && data.order.length === 0 && (
                <p className="px-4 py-6 text-center text-xs text-slate-500">
                  No template switches on for this order. Set them up in Settings → Trip Checkpoints, or add a
                  one-off above.
                </p>
              )}
            </div>
          )}
        </div>

        <footer className="flex items-center justify-between gap-3 border-t border-slate-200 px-4 py-2">
          <p className="text-[10px] text-slate-400">
            Nothing here stops the order — open and overdue checkpoints are warnings. Driver and subcontractor items are
            done in myDriver.
          </p>
          {onSendToDriver && (
            <button
              type="button"
              onClick={onSendToDriver}
              className="inline-flex h-8 shrink-0 items-center gap-1.5 rounded-md bg-[#002E72] px-3 text-xs font-semibold text-white hover:bg-[#00224B]"
            >
              <Send className="h-3.5 w-3.5" /> Send to driver
            </button>
          )}
        </footer>
      </aside>
    </>
  );
}

function Summary({ data }: { data: OrderCheckpoints }) {
  const s = data.summary;
  const bits: ReactNode[] = [];
  if (s.overdue) bits.push(<span key="o" className="font-medium text-amber-800">{s.overdue} overdue</span>);
  if (s.dueNext) bits.push(<span key="d" className="text-amber-700">{s.dueNext} due next</span>);
  bits.push(
    <span key="r">
      {s.openRequired} required open
      {s.openRequired > 0 && ` (${s.plannerOpenRequired} office · ${s.onSiteOpenRequired} on site)`}
    </span>,
  );
  return (
    <p className="mt-0.5 flex flex-wrap gap-x-2 text-[11px] text-slate-500">
      {bits.map((b, i) => (
        <span key={i} className="flex items-center gap-2">
          {i > 0 && <span className="text-slate-300">·</span>}
          {b}
        </span>
      ))}
    </p>
  );
}

function Section({ title, subtitle, children }: { title: ReactNode; subtitle?: string; children: ReactNode }) {
  return (
    <section className="px-4 py-3">
      <div className="mb-1.5">
        <div className="text-xs font-semibold text-slate-700">{title}</div>
        {subtitle && <p className="text-[10px] text-slate-400">{subtitle}</p>}
      </div>
      {children}
    </section>
  );
}

// ── Items ───────────────────────────────────────────────────────────────────

interface ItemContext {
  actions: ReturnType<typeof useOrderCheckpointActions>;
  run: (action: () => Promise<unknown>, failed: string) => Promise<void>;
  canWaive: boolean;
  /** For the phone's photos and signatures. */
  organisationId: string | null;
  orderId: string | null;
}

function Items({ items, orderTaskId, ctx }: { items: OrderCheckpointItem[]; orderTaskId: string | null; ctx: ItemContext }) {
  if (items.length === 0) return <p className="py-1 text-[11px] text-slate-400">Nothing to check.</p>;
  return (
    <ul className="space-y-2">
      {items.map((item) => (
        <ItemRow key={item.key} item={item} orderTaskId={orderTaskId} ctx={ctx} />
      ))}
    </ul>
  );
}

function ItemRow({ item, orderTaskId, ctx }: { item: OrderCheckpointItem; orderTaskId: string | null; ctx: ItemContext }) {
  const c = checkpointFromWire(item.checkpoint);
  const [skipping, setSkipping] = useState<'NotApplicable' | 'Waived' | null>(null);
  const [reason, setReason] = useState('');
  const [value, setValue] = useState('');
  const { actions, run, canWaive } = ctx;
  // Set while THIS row's action is saving, so the spinner shows where the
  // click was — not on every row at once.
  const [saving, setSaving] = useState(false);
  const act = async (action: () => Promise<unknown>, failed: string) => {
    setSaving(true);
    try {
      await run(action, failed);
    } finally {
      setSaving(false);
    }
  };

  const planner = item.signOffs.find((s) => s.role === 'Planner');
  const plannerOpen = item.state === 'Open' && planner && !planner.done;
  // A tick is the whole answer for these kinds, so the circle itself is the
  // control: click to mark the planner's part done, click again to undo it.
  // Answer and value kinds keep their buttons / input below.
  const simple = !ANSWER_KINDS.includes(c.kind) && !VALUE_KINDS.includes(c.kind);
  const plannerRecord = planner?.record ?? null;
  const canTick = simple && !!plannerOpen && !skipping;
  const canUntick = simple && plannerRecord?.outcome === 'Done' && plannerRecord.source === 'Office';
  // The planner has signed, the driver / sub still has to.
  const halfDone = item.state === 'Open' && !!planner?.done;
  const timing = item.checkpoint.due
    ? DUE_LABEL[c.due]
    : item.checkpoint.moment
      ? MOMENT_LABEL[c.moment]
      : '';

  if (item.skipped) {
    return (
      <li className="flex items-start gap-2 opacity-50">
        <MinusCircle className="mt-0.5 h-4 w-4 shrink-0 text-slate-300" />
        <div className="min-w-0">
          <p className="text-xs text-slate-700 line-through">{c.label}</p>
          <p className="text-[10px] text-slate-400">{item.skipped}</p>
        </div>
      </li>
    );
  }

  const done = (answer: boolean | null, valueText: string | null) =>
    act(
      () => actions.done.mutateAsync({ orderTaskId, key: item.key, answer, valueText }),
      'Failed to record it.',
    ).then(() => setValue(''));

  const skip = () =>
    act(
      () => actions.skip.mutateAsync({ orderTaskId, key: item.key, outcome: skipping!, reason: reason.trim() }),
      'Failed to update it.',
    ).then(() => {
      setSkipping(null);
      setReason('');
    });

  return (
    <li
      className={cn(
        'rounded-md border px-2.5 py-2',
        item.dueState === 'Overdue'
          ? 'border-amber-300 bg-amber-50'
          : item.dueState === 'DueNext'
            ? 'border-amber-200 bg-amber-50/40'
            : 'border-slate-200',
      )}
    >
      <div className="flex items-start gap-2">
        {saving ? (
          <Loader2 className="mt-0.5 h-4 w-4 shrink-0 animate-spin text-sky-600" aria-label="Saving" />
        ) : canTick || canUntick ? (
          <button
            type="button"
            onClick={() =>
              canTick
                ? done(null, null)
                : act(() => actions.undo.mutateAsync(plannerRecord!.id), 'Failed to undo it.')
            }
            disabled={actions.busy}
            title={canTick ? 'Mark done' : 'Undo'}
            aria-label={canTick ? `Mark “${c.label}” done` : `Undo “${c.label}”`}
            className="group/tick mt-0.5 shrink-0 rounded-full disabled:opacity-50"
          >
            {canTick ? (
              <span className="flex h-4 w-4 items-center justify-center rounded-full border-2 border-slate-300 transition-colors group-hover/tick:border-emerald-500 group-hover/tick:bg-emerald-50">
                <Check className="h-2.5 w-2.5 text-emerald-600 opacity-0 group-hover/tick:opacity-100" />
              </span>
            ) : (
              <StateIcon state={item.state} halfDone={halfDone} />
            )}
          </button>
        ) : (
          <StateIcon state={item.state} halfDone={halfDone} />
        )}
        <div className="min-w-0 flex-1">
          <p className={cn('text-xs text-slate-800', item.state !== 'Open' && item.state !== 'Done' && 'line-through')}>
            {c.label || 'Untitled checkpoint'}
            {!c.required && <span className="text-slate-400"> · optional</span>}
          </p>
          {c.hint && <p className="text-[10px] text-slate-500">{c.hint}</p>}
          <p className="text-[10px] text-slate-400">
            {KINDS[c.kind].label}
            {timing && ` · ${timing}`} · {item.sources.join(' + ')}
          </p>
          {item.state === 'Open' && item.dueState === 'Overdue' && (
            <p className="mt-0.5 text-[10px] font-medium text-amber-800">
              Overdue — the order has moved past {DUE_LABEL[c.due].toLowerCase().replace('before ', '')}
            </p>
          )}
          {item.state === 'Open' && item.dueState === 'DueNext' && (
            <p className="mt-0.5 text-[10px] text-amber-700">Due next — {DUE_LABEL[c.due]}</p>
          )}
        </div>
        <span className="flex shrink-0 items-center gap-0.5">
          {item.signOffs.map((s) => (
            <span key={s.role} className={cn('flex items-center', !s.done && 'opacity-60')} title={s.done ? 'Signed' : 'Waiting'}>
              <RoleTag role={roleFromWire(s.role)} />
              {s.done && <Check className="h-3 w-3 text-emerald-600" />}
            </span>
          ))}
          {!item.checkpoint.waivable && (
            <span title="Can't be waived or marked not applicable" className="text-slate-400">
              <Lock className="h-3 w-3" />
            </span>
          )}
        </span>
      </div>

      {/* What was recorded */}
      {item.signOffs
        .filter((s) => s.record)
        .map((s) => (
          <div key={s.record!.id}>
            <RecordLine
              record={s.record!}
              onUndo={
                simple && s.role === 'Planner'
                  ? undefined
                  : () => act(() => actions.undo.mutateAsync(s.record!.id), 'Failed to undo it.')
              }
              busy={actions.busy}
            />
            <CheckpointEvidence record={s.record!} organisationId={ctx.organisationId} orderId={ctx.orderId} />
          </div>
        ))}
      {item.skip && (
        <RecordLine
          record={item.skip}
          onUndo={
            item.skip.outcome === 'Waived' && !canWaive
              ? undefined
              : () => act(() => actions.undo.mutateAsync(item.skip!.id), 'Failed to undo it.')
          }
          busy={actions.busy}
        />
      )}

      {/* The planner's part */}
      {plannerOpen && !simple && !skipping && (
        <div className="mt-2 flex flex-wrap items-center gap-1.5 pl-6">
          {ANSWER_KINDS.includes(c.kind) ? (
            <>
              <SmallButton onClick={() => done(true, null)} disabled={actions.busy}>
                {c.kind === 'inspection' ? 'Pass' : 'Yes'}
              </SmallButton>
              <SmallButton onClick={() => done(false, null)} disabled={actions.busy}>
                {c.kind === 'inspection' ? 'Fail' : 'No'}
              </SmallButton>
            </>
          ) : VALUE_KINDS.includes(c.kind) ? (
            <>
              <input
                value={value}
                onChange={(e) => setValue(e.target.value)}
                maxLength={500}
                placeholder={c.kind === 'note' ? 'Note' : c.kind === 'identifier' ? 'Number' : 'Value'}
                className="h-7 w-40 rounded border border-slate-300 px-2 text-xs focus:border-sky-400 focus:outline-none"
              />
              <SmallButton onClick={() => done(null, value.trim() || null)} disabled={actions.busy || !value.trim()}>
                Save
              </SmallButton>
            </>
          ) : null}
        </div>
      )}

      {/* Taking it off the order */}
      {item.state === 'Open' && item.checkpoint.waivable && !skipping && (
        <div className="mt-1.5 flex gap-3 pl-6 text-[11px]">
          <button type="button" onClick={() => setSkipping('NotApplicable')} className="text-slate-500 hover:text-slate-800">
            Not applicable…
          </button>
          {canWaive && (
            <button type="button" onClick={() => setSkipping('Waived')} className="text-slate-500 hover:text-slate-800">
              Waive…
            </button>
          )}
        </div>
      )}
      {skipping && (
        <div className="mt-2 flex flex-wrap items-center gap-1.5 pl-6">
          <input
            autoFocus
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            maxLength={500}
            placeholder={skipping === 'Waived' ? 'Why it is being waived' : 'Why it does not apply'}
            className="h-7 min-w-0 flex-1 rounded border border-slate-300 px-2 text-xs focus:border-sky-400 focus:outline-none"
          />
          <SmallButton onClick={skip} disabled={actions.busy || !reason.trim()}>
            {skipping === 'Waived' ? 'Waive' : 'Not applicable'}
          </SmallButton>
          <button type="button" onClick={() => setSkipping(null)} className="text-[11px] text-slate-500 hover:text-slate-800">
            Cancel
          </button>
        </div>
      )}

      {item.adhocId && (
        <div className="mt-1.5 flex items-center justify-between pl-6">
          <span className="text-[10px] text-violet-700">Added on this order</span>
          <button
            type="button"
            onClick={() => act(() => actions.remove.mutateAsync(item.adhocId!), 'Failed to remove it.')}
            disabled={actions.busy}
            className="inline-flex items-center gap-1 text-[11px] text-slate-400 hover:text-red-600"
          >
            <Trash2 className="h-3 w-3" /> Remove
          </button>
        </div>
      )}
    </li>
  );
}

function StateIcon({ state, halfDone = false }: { state: OrderCheckpointItem['state']; halfDone?: boolean }) {
  if (state === 'Open' && halfDone)
    return (
      <span
        title="Signed in the office — waiting on site"
        className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full border-2 border-emerald-500"
      >
        <Check className="h-2.5 w-2.5 text-emerald-600" />
      </span>
    );
  switch (state) {
    case 'Done':
      return <Check className="mt-0.5 h-4 w-4 shrink-0 rounded-full bg-emerald-100 p-0.5 text-emerald-700" />;
    case 'Waived':
      return <Ban className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" />;
    case 'NotApplicable':
      return <MinusCircle className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" />;
    default:
      return <Circle className="mt-0.5 h-4 w-4 shrink-0 text-slate-300" />;
  }
}

function RecordLine({ record: r, onUndo, busy }: { record: OrderCheckpointRecord; onUndo?: () => void; busy: boolean }) {
  const failed = r.answer === false;
  const what =
    r.outcome === 'Waived'
      ? `Waived — ${r.reason ?? ''}`
      : r.outcome === 'NotApplicable'
        ? `Not applicable — ${r.reason ?? ''}`
        : [
            // A number (temperature, count, reading), a typed value, then the
            // pass / fail it was judged as — "4.2 · Failed …" for a reading out of range.
            r.numberValue != null ? String(r.numberValue) : null,
            r.valueText,
            r.answer === true && r.numberValue == null && !r.valueText ? 'Passed' : null,
            r.answer === false ? 'Failed — the planner is warned' : null,
          ]
            .filter(Boolean)
            .join(' · ') || 'Done';
  return (
    <div className="mt-1 flex items-center justify-between gap-2 pl-6 text-[11px]">
      <span className={cn('min-w-0', failed ? 'font-medium text-amber-800' : 'text-slate-600')}>
        {what}
        <span className="text-slate-400">
          {' '}
          · {r.recordedByName} · {when(r.recordedUtc)}
          {r.source === 'Phone' && ' · on the phone'}
        </span>
      </span>
      {onUndo && r.source === 'Office' && (
        <button
          type="button"
          onClick={onUndo}
          disabled={busy}
          className="inline-flex shrink-0 items-center gap-1 text-slate-400 hover:text-slate-700"
        >
          <Undo2 className="h-3 w-3" /> Undo
        </button>
      )}
    </div>
  );
}

function SmallButton({
  onClick,
  disabled,
  children,
}: {
  onClick: () => void;
  disabled?: boolean;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="inline-flex h-7 items-center gap-1 rounded-md border border-slate-300 bg-white px-2.5 text-[11px] font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
    >
      {children}
    </button>
  );
}

// ── One-offs ────────────────────────────────────────────────────────────────

function AddOneOff({ orderTaskId, ctx }: { orderTaskId: string | null; ctx: ItemContext }) {
  const isOrder = orderTaskId === null;
  const [open, setOpen] = useState(false);
  const [label, setLabel] = useState('');
  const [kind, setKind] = useState<CheckpointKind>('confirm');
  const [roles, setRoles] = useState<Role[]>(isOrder ? ['planner'] : ['driver', 'sub']);
  const [required, setRequired] = useState(true);
  const [due, setDue] = useState<Due>('beforeInProgress');
  const [moment, setMoment] = useState<Moment>('during');

  const onSiteOnly = KINDS[kind].onSiteOnly;
  const effectiveRoles = onSiteOnly ? roles.filter((r) => r !== 'planner') : roles;

  const reset = () => {
    setOpen(false);
    setLabel('');
    setKind('confirm');
    setRoles(isOrder ? ['planner'] : ['driver', 'sub']);
    setRequired(true);
  };

  const save = () =>
    ctx
      .run(
        () =>
          ctx.actions.add.mutateAsync({
            orderTaskId,
            label: label.trim(),
            hint: null,
            kind: `${kind[0]!.toUpperCase()}${kind.slice(1)}` as TripCheckpointKindValue,
            roles: (effectiveRoles.length ? effectiveRoles : (['driver', 'sub'] as Role[])).map(roleToWire),
            required,
            due: isOrder ? (`${due[0]!.toUpperCase()}${due.slice(1)}` as TripCheckpointDueValue) : null,
            moment: isOrder
              ? null
              : moment === 'arrival'
                ? 'OnArrival'
                : moment === 'during'
                  ? 'During'
                  : 'BeforeDeparture',
            configJson: null,
          }),
        'Failed to add it.',
      )
      .then(reset);

  if (!open)
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="mt-2 inline-flex items-center gap-1 text-[11px] text-sky-700 hover:text-sky-900"
      >
        <Plus className="h-3 w-3" /> Add a one-off
      </button>
    );

  const select =
    'h-7 rounded border border-slate-300 bg-white px-1.5 text-[11px] text-slate-700 focus:border-sky-400 focus:outline-none';
  return (
    <div className="mt-2 space-y-1.5 rounded-md border border-sky-200 bg-sky-50/50 p-2">
      <input
        autoFocus
        value={label}
        onChange={(e) => setLabel(e.target.value)}
        maxLength={160}
        placeholder="What has to be checked on this order?"
        className="h-7 w-full rounded border border-slate-300 bg-white px-2 text-xs focus:border-sky-400 focus:outline-none"
      />
      <div className="flex flex-wrap items-center gap-1.5">
        <select value={kind} onChange={(e) => setKind(e.target.value as CheckpointKind)} className={select} aria-label="How it is checked">
          {KIND_ORDER.map((k) => (
            <option key={k} value={k}>
              {KINDS[k].label}
            </option>
          ))}
        </select>
        <RoleToggles roles={effectiveRoles.length ? effectiveRoles : roles} onChange={setRoles} plannerDisabled={onSiteOnly} />
        {isOrder ? (
          <select value={due} onChange={(e) => setDue(e.target.value as Due)} className={select} aria-label="Due">
            {DUE_ORDER.map((d) => (
              <option key={d} value={d}>
                {DUE_LABEL[d]}
              </option>
            ))}
          </select>
        ) : (
          <select value={moment} onChange={(e) => setMoment(e.target.value as Moment)} className={select} aria-label="When at the stop">
            {MOMENT_ORDER.map((m) => (
              <option key={m} value={m}>
                {MOMENT_LABEL[m]}
              </option>
            ))}
          </select>
        )}
        <label className="flex items-center gap-1 text-[11px] text-slate-700">
          <input type="checkbox" checked={required} onChange={(e) => setRequired(e.target.checked)} /> Required
        </label>
      </div>
      <div className="flex justify-end gap-2">
        <button type="button" onClick={reset} className="text-[11px] text-slate-500 hover:text-slate-800">
          Cancel
        </button>
        <SmallButton onClick={save} disabled={ctx.actions.busy || !label.trim()}>
          Add
        </SmallButton>
      </div>
    </div>
  );
}
